/**
 * Formatting utilities for forecast data
 */

/**
 * Format a number as currency
 * @param {number} value - The value to format
 * @returns {string} Formatted currency string, '-' for zero, blank for null/undefined
 */
export const formatCurrency = (value) => {
    if (value === null || value === undefined) return '';
    if (value === 0) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currencySign: 'accounting'
    }).format(value);
};

/**
 * Format a number as compact currency (e.g., $1.2K, $3.5M)
 * @param {number} value - The value to format
 * @returns {string} Formatted compact currency string
 */
export const formatCompactCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        notation: 'compact'
    }).format(value);
};

/**
 * Format a date string based on granularity
 * @param {string} dateStr - ISO date string (YYYY-MM-DD)
 * @param {string} granularity - 'week', 'month', or 'day'
 * @returns {string} Formatted date string
 */
export const formatPeriodLabel = (dateStr, granularity) => {
    const date = new Date(dateStr + 'T00:00:00Z');
    
    if (granularity === 'week') {
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            timeZone: 'UTC'
        });
    } else if (granularity === 'month') {
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            year: 'numeric',
            timeZone: 'UTC'
        });
    } else {
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            timeZone: 'UTC'
        });
    }
};

/**
 * Get period label prefix (W1, M1, D1, etc.)
 * @param {number} index - 0-based period index
 * @param {string} granularity - 'week', 'month', or 'day'
 * @returns {string} Period prefix (e.g., 'W1', 'M1')
 */
export const getPeriodPrefix = (index, granularity) => {
    const prefix = granularity === 'week' ? 'W' : 
                   granularity === 'month' ? 'M' : 'D';
    return `${prefix}${index + 1}`;
};

/**
 * Get granularity display name (plural)
 * @param {string} granularity - 'week', 'month', or 'day'
 * @returns {string} Display name ('weeks', 'months', 'days')
 */
export const getGranularityName = (granularity) => {
    if (granularity === 'week') return 'weeks';
    if (granularity === 'month') return 'months';
    return 'days';
};

/**
 * Format a number as percentage
 * @param {number} value - The value to format (0.05 = 5%)
 * @returns {string} Formatted percentage string, '-' for zero, blank for null/undefined
 */
export const formatPercent = (value) => {
    if (value === null || value === undefined) return '';
    if (value === 0) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    }).format(value);
};

/**
 * Format a number as a count/integer
 * @param {number} value - The value to format
 * @returns {string} Formatted count string, '-' for zero, blank for null/undefined
 */
export const formatCount = (value) => {
    if (value === null || value === undefined) return '';
    if (value === 0) return '-';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    }).format(value);
};

/**
 * Format a value based on its valueType
 * @param {number} value - The value to format
 * @param {string} type - The valueType from the node ('currency', 'percentage', 'percent', 'number', 'count', or null)
 * @returns {string} Formatted value string, empty string for null valueType or null/undefined values, '-' for actual 0 values
 */
export const formatValue = (value, type = 'currency') => {
    // If valueType is null, return empty string (no display)
    if (type === null) return '';
    
    // Otherwise format based on valueType
    // null/undefined -> blank, 0 -> '-'
    if (value === null || value === undefined) return '';
    
    switch (type) {
        case 'percentage':
        case 'percent':
            return formatPercent(value);
        case 'number':
        case 'count':
            return formatCount(value);
        case 'currency':
        default:
            return formatCurrency(value);
    }
};

